/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { Form, Modal } from 'antd';
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { StatusText } from '@/components/StatusText';
import { useMessage } from '@/hooks/message';
import { ClearTelemetryParams, useClearTelemetryMutation } from '@/hooks/mutation/useClearTelemetryMutation';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { ClearTelemetryForm } from '@/pages/Telemetry/ClearTelemetryForm';

const clearTelemetryParams = z.object({
  telemetryType: z.enum(['hallmarks', 'deflections', 'signals']),
  mimicIds: z.array(z.string()).optional(),
  telemetryEventIds: z.array(z.string()).optional(),
  type: z.enum(['selected', 'all']),
});

export function ClearTelemetryModal() {
  const { t } = useMimicTranslation('node');
  const navigate = useNavigate();
  const { state } = useLocation();
  const params = useParams<'id'>();
  const nodeId = params.id;
  const [open, setOpen] = useState(true);
  const [status, setStatus] = useState<'unconfirmed' | 'pending' | 'confirmed'>('unconfirmed');
  const [form] = Form.useForm<ClearTelemetryParams>();
  const [message] = useMessage();
  const { mutate } = useClearTelemetryMutation(nodeId!, {
    onSuccess: () => {
      afterSuccess();
    },
    onError: () => {
      message.error(t('clearTelemetryModal.error'));
    },
  });

  const safeState = clearTelemetryParams.safeParse(state);
  if (!safeState.success) {
    message.error(t('clearTelemetryModal.error'));
    return null;
  }
  const { telemetryType } = safeState.data;

  const closeModal = () => {
    setOpen(false);
  };

  const afterClose = () => {
    navigate(-1);
  };

  const afterSuccess = () => {
    setStatus('confirmed');
    form.resetFields();

    setTimeout(() => {
      setStatus('unconfirmed');
    }, 2000);
  };

  async function validateAndSubmit() {
    try {
      setStatus('pending');
      const formParams = await form.validateFields();

      mutate({
        reason: formParams.reason,
        telemetryType,
        mimicIds: safeState.data?.mimicIds ?? [],
        telemetryEventIds: safeState.data?.telemetryEventIds ?? [],
        type: safeState.data?.type ?? 'selected',
      });
    } catch (error) {
      setStatus('unconfirmed');
    }
  }

  return (
    <Modal
      title={t(`clearTelemetryModal.${telemetryType}.title`)}
      centered
      afterClose={afterClose}
      open={open}
      onOk={() => validateAndSubmit()}
      onCancel={closeModal}
      confirmLoading={status === 'pending'}
      okButtonProps={{ disabled: status === 'pending' }}
      cancelButtonProps={{ disabled: status === 'pending' }}
      okText={
        <StatusText
          dataTestId={`clear-${telemetryType}-confirm`}
          status={status}
          text={{
            unconfirmed: t(`clearTelemetryModal.${telemetryType}.buttonTextUnconfirmed`),
            pending: t('clearTelemetryModal.buttonTextPending'),
            confirmed: t('clearTelemetryModal.buttonTextConfirmed'),
          }}
        />
      }
    >
      <ClearTelemetryForm telemetryType={telemetryType} form={form} />
    </Modal>
  );
}
